/**
 * Subscriptions / Payment API — 订阅与支付相关接口
 *
 * 统一走 ApiClient，自动处理 401 token 刷新、错误包装等。
 */
import { ApiClient } from './client';
import type { ApiResponse } from './types';
import type { CreditUnitMetadata } from '@/lib/credits/unit';
import { generateId } from '@/lib/utils';
import { getAcquisitionVisitClaim } from '@/lib/marketing/acquisition';
import { trackFunnelEvent } from '@/lib/marketing/funnel';
import { captureXConversionOwner, trackXCheckout, trackXPurchase } from '@/lib/marketing/x-conversions';

const PAYMENT_MUTATION_TIMEOUT_MS = 30_000;
const PAYMENT_STATUS_TIMEOUT_MS = 8_000;

function boundedPaymentSignal(timeoutMs: number): AbortSignal {
  if (typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(timeoutMs);
  }
  const controller = new AbortController();
  window.setTimeout(() => controller.abort(), timeoutMs);
  return controller.signal;
}

// ── Types ──

export interface AdminMonthlyTestOffer {
  id: string;
  plan_slug: string;
  price_monthly: number;
  credits_per_month: number;
  currency: string;
  configured: boolean;
}

export interface AlipayPaymentResponse {
  success: boolean;
  mode: 'alipay';
  order_id: string;
  pay_url: string;
  plan_slug?: string;
  amount_yuan: number;
  original_amount_yuan?: number;
  coupon_claim_id?: string | null;
  coupon_code?: string | null;
  coupon_discount_yuan?: number;
  currency?: string;
  payment_provider?: string | null;
  subscription_discount_yuan?: number;
  subscription_discount_pct?: number;
  subscription_discount_plan?: string | null;
  credits_amount?: number;
  bonus_credits?: number;
}

export interface WechatPaymentResponse {
  success: boolean;
  mode: 'wechat_native';
  order_id: string;
  code_url: string;
  plan_slug?: string;
  amount_yuan: number;
  original_amount_yuan?: number;
  coupon_claim_id?: string | null;
  coupon_code?: string | null;
  coupon_discount_yuan?: number;
  currency?: string;
  payment_provider?: string | null;
  subscription_discount_yuan?: number;
  subscription_discount_pct?: number;
  subscription_discount_plan?: string | null;
  credits_amount?: number;
  bonus_credits?: number;
}

export interface TopupResponse {
  success: boolean;
  mode: 'airwallex' | 'dev';
  checkout_url?: string;
  checkout_id?: string;
  order_id: string;
  credits_amount?: number;
  credits_purchased?: number;
  bonus_credits: number;
  total_credits?: number;
  amount_cents: number;
  amount_yuan: number;
  currency?: string;
  original_amount_yuan?: number;
  coupon_claim_id?: string | null;
  coupon_code?: string | null;
  coupon_discount_yuan?: number;
  subscription_discount_yuan?: number;
  subscription_discount_pct?: number;
  subscription_discount_plan?: string | null;
  message?: string;
}

export interface AirwallexCheckoutResponse {
  success: boolean;
  mode: 'airwallex';
  order_id: string;
  checkout_url: string;
  checkout_id?: string | null;
  client_secret?: string | null;
  plan_slug?: string;
  amount_yuan: number;
  currency?: string;
  credits_amount?: number;
  bonus_credits?: number;
  amount_cents?: number;
  original_amount_yuan?: number;
  coupon_claim_id?: string | null;
  coupon_code?: string | null;
  coupon_discount_yuan?: number;
  subscription_discount_yuan?: number;
  subscription_discount_pct?: number;
  subscription_discount_plan?: string | null;
}

export interface WechatCycleSubscribeResponse {
  success: boolean;
  mode: 'wechat_cycle';
  contract_code: string;
  plan_slug: string;
  amount_yuan: number;
  sign_url?: string | null;
  sign_scene?: 'h5' | 'jsapi' | 'mini_program' | 'mini-program' | string | null;
  mini_program?: {
    qr_code_data_url?: string | null;
    scene?: string | null;
    path?: string | null;
    env_version?: string | null;
    // 兼容切换前尚未过期的 URL Link 响应。
    pre_entrustweb_id?: string | null;
    redirect_appid?: string | null;
    redirect_path?: string | null;
    url_link?: string | null;
    url_link_payload?: Record<string, unknown> | null;
  } | null;
  agreement?: Record<string, unknown>;
}

export interface AgreementStatusData {
  provider: 'wechatpay';
  agreement_no?: string | null;
  contract_code?: string | null;
  plan_slug?: string | null;
  status?: string | null;
  signed_at?: string | null;
  next_deduct_at?: string | null;
  deduct_count?: number | null;
  single_amount?: string | null;
}

export interface WechatCycleSessionData {
  contract_code: string;
  status: 'unsigned' | 'signed' | 'failed' | 'terminated' | string;
  plan_slug: string;
  plan_name: string;
  amount_yuan: number;
  resign_only: boolean;
  subscription_action: 'subscribed' | 'upgraded' | 'resign_only' | string;
}

export interface OrderStatusResponse {
  paid_at?: string | null;
  success: boolean;
  status: 'pending' | 'processing' | 'paid' | 'expired' | 'amount_mismatch' | 'limit_rejected' | 'resolved' | 'refunded' | 'ignored';
  order_type: 'topup' | 'subscription' | 'renewal';
  plan_slug?: string;
  amount_yuan?: number;
  original_amount_yuan?: number;
  coupon_claim_id?: string | null;
  coupon_code?: string | null;
  coupon_discount_yuan?: number;
  currency?: string;
  payment_provider?: string | null;
  subscription_discount_yuan?: number;
  subscription_discount_pct?: number;
  subscription_discount_plan?: string | null;
  total_credits?: number;
  credit_unit_mode?: 'dobi' | string;
  unit_key?: string;
  unit_label_zh?: string;
  unit_label_en?: string;
  scale?: number;
  credits_per_image?: number;
  credit_unit?: CreditUnitMetadata | null;
}

export interface ExistingOrderAlipayPayUrlResponse {
  success: boolean;
  mode: 'alipay';
  order_id: string;
  pay_url: string;
  amount_yuan: number;
}

export interface ResumeOrderCheckoutResponse {
  success: boolean;
  mode: 'airwallex';
  order_id: string;
  checkout_url: string;
  checkout_id?: string | null;
  client_secret?: string | null;
  plan_slug?: string | null;
  credits_amount?: number | null;
  amount_yuan: number;
  currency: string;
}

export interface AirwallexCheckoutSession {
  success: boolean;
  order: UserOrderSummary;
  environment: 'prod' | 'demo';
  success_url: string;
  status: 'active' | 'completed' | 'hosted' | 'processing';
  flow?: 'PAYMENTS';
  payment_mode?: 'payment' | 'recurring';
  customer_id?: string;
  customer_email?: string;
  intent_id?: string;
  methods?: string[];
  merchant_country?: string;
  client_secret?: string;
  checkout_url?: string;
}

export interface ExpireOrderResponse {
  success: boolean;
  expired: boolean;
  status: UserOrderSummary['status'];
}

export interface SwitchPaymentMethodResponse {
  success: boolean;
  order_id: string;
  status: 'paid' | 'expired';
  can_create_replacement: boolean;
  released_coupon_claim_id?: string | null;
}

export interface OpenWechatTopupOrder {
  order_id: string;
  status: 'pending' | 'processing';
  pack_slug?: string | null;
  code_url?: string | null;
  amount_yuan: number;
  coupon_claim_id?: string | null;
  created_at?: string | null;
  quantity?: number;
}

export interface InvoiceRequestSummary {
  id: string;
  out_trade_no: string;
  order_type: 'topup' | 'subscription' | 'renewal' | 'cycle_deduct' | 'cycle_pay';
  plan_slug?: string | null;
  order_title: string;
  amount_yuan: number;
  currency?: string | null;
  payment_provider?: string | null;
  invoice_title_type: 'personal' | 'company';
  invoice_type: 'ordinary';
  invoice_title: string;
  tax_number?: string | null;
  email: string;
  remark?: string | null;
  status: 'pending' | 'processing' | 'issued' | 'rejected';
  admin_note?: string | null;
  invoice_number?: string | null;
  invoice_url?: string | null;
  processed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserOrderSummary {
  plan_change?: boolean;
  checkout_cancelled?: boolean;
  payment_declined?: boolean;
  payment_failure_code?: string | null;
  out_trade_no: string;
  order_type: 'topup' | 'subscription' | 'renewal' | 'cycle_deduct' | 'cycle_pay';
  plan_slug?: string | null;
  order_title: string;
  credits_amount: number;
  bonus_credits: number;
  total_credits: number;
  amount_yuan: number;
  original_amount_yuan?: number;
  coupon_claim_id?: string | null;
  coupon_code?: string | null;
  coupon_discount_yuan?: number;
  currency?: string;
  payment_provider?: string | null;
  subscription_discount_yuan?: number;
  subscription_discount_pct?: number;
  subscription_discount_plan?: string | null;
  credit_unit_mode?: 'dobi' | string;
  unit_key?: string;
  unit_label_zh?: string;
  unit_label_en?: string;
  scale?: number;
  credits_per_image?: number;
  credit_unit?: CreditUnitMetadata | null;
  status: 'pending' | 'processing' | 'paid' | 'expired' | 'amount_mismatch' | 'limit_rejected' | 'resolved' | 'refunded' | 'ignored';
  trade_no?: string | null;
  paid_at?: string | null;
  created_at: string;
  updated_at: string;
  invoice_status?: InvoiceRequestSummary['status'] | null;
  invoice_request?: InvoiceRequestSummary | null;
}

export interface SubmitInvoicePayload {
  invoice_title_type: 'personal' | 'company';
  invoice_type: 'ordinary';
  invoice_title: string;
  tax_number?: string;
  email: string;
  remark?: string;
}

export interface CurrentSubscription {
  id?: string | null;
  status?: string;
  is_free?: boolean;
  billing_cycle?: string;
  current_period_start?: string | null;
  current_period_end?: string | null;
  auto_renew?: boolean;
  canceled_at?: string | null;
  payment_method?: string | null;
  provider?: string | null;
  management_channel?: 'manage_in_apple' | 'manage_on_web' | 'manual_renew_on_web' | 'contact_support' | string | null;
  available_actions?: string[];
  web_checkout_eligibility?: {
    allowed: boolean;
    blocked_reason?: string | null;
    apple_renewal_verified?: boolean;
  } | null;
  renewal_status?: {
    last_failed_at?: string | null;
    last_error?: string | null;
    last_failed_provider?: string | null;
    last_failed_order_id?: string | null;
    last_deduct_at?: string | null;
    apple_will_auto_renew_verified?: boolean | null;
  } | null;
  plan?: {
    slug: string;
    name: string;
    price_monthly?: number;
    credits_per_month?: number;
    price_variant_id?: string | null;
    features?: Record<string, unknown>;
  };
}

export interface SubscriptionEntitlementPlan {
  slug: string;
  name: string;
  features: Record<string, unknown>;
}

export interface SubscriptionEntitlementContext {
  surface: 'web';
  actual_plan_slug: string;
  base_plan_slug: string;
  effective_plan: SubscriptionEntitlementPlan;
  source: 'actual_subscription' | 'campaign' | 'free';
}

export interface CurrentSubscriptionResponse {
  success: boolean;
  subscription?: CurrentSubscription;
  /** Web-only access context. Billing and wallet code must keep using `subscription` and `credits`. */
  entitlement?: SubscriptionEntitlementContext;
  expired_subscription?: {
    plan_slug: string;
    plan_name: string;
    expired_at: string;
    price_monthly?: number;
    credits_per_month?: number;
    price_variant_id?: string | null;
  } | null;
  credits?: {
    balance: number;
    wallet_frozen?: boolean;
    free_credit_policy?: 'daily' | 'welcome_once';
    welcome_credit_amount?: number;
    welcome_credit_status?: 'pending' | 'granted' | 'legacy_satisfied' | 'ineligible' | null;
    welcome_credit_granted_at?: string | null;
    subscription_credits_remaining: number;
    subscription_credits_expires_at?: string | null;
    purchased_credits: number;
    total_granted: number;
    total_consumed: number;
    storage_used_mb?: number;
    credit_unit_mode?: 'dobi' | string;
    unit_key?: string;
    unit_label_zh?: string;
    unit_label_en?: string;
    scale?: number;
    credits_per_image?: number;
    credit_unit?: CreditUnitMetadata | null;
  };
}

export interface SubscriptionPlanResponse {
  slug: string;
  name: string;
  description: string;
  price_monthly: number;
  credits_per_month: number;
  welcome_credits?: number;
  registration_credits?: number;
  approx_image_count?: number;
  approx_video_count?: number;
  /** Kling 4.0 Omni · 720p · 5s · no video reference. */
  video_estimate_credits?: number;
  bonus_credits: number;
  features: Record<string, unknown>;
  is_popular: boolean;
  badge_text?: string;
  display_order: number;
  price_options?: SubscriptionPriceOptionResponse[];
}

export interface SubscriptionPriceOptionResponse {
  id: string;
  plan_slug: string;
  price_monthly: number;
  credits_per_month: number;
  is_default: boolean;
  is_purchasable: boolean;
  display_order: number;
}

export interface SubscriptionPlansListResponse {
  success: boolean;
  currency: string;
  plans: SubscriptionPlanResponse[];
  credit_unit_mode?: 'dobi' | string;
  unit_key?: string;
  unit_label_zh?: string;
  unit_label_en?: string;
  scale?: number;
  credits_per_image?: number;
  credit_unit?: CreditUnitMetadata | null;
}

export interface MembershipUseState {
  kind: 'template';
  scope_key: string;
  plan_slug: string;
  period: 'lifetime' | 'utc_day' | 'unlimited';
  used: number;
  limit: number | null;
  remaining: number | null;
  locked: boolean;
  reset_at?: string | null;
}

export type SubscriptionPlanSurface = 'web' | 'app';

export interface TopupPackResponse {
  slug: string;
  credits: number;
  bonus_credits: number;
  total_credits: number;
  price_cny: number;
  price_cents?: number;
  image_count: number;
  unit_price_cny: number;
  name: string;
  description: string;
  original_price_cny?: number | null;
  discount_pct?: number;
  is_popular?: boolean;
  admin_only?: boolean;
  requires_subscription?: boolean;
  purchase_limit?: {
    window_days: number;
    max_purchases: number;
  } | null;
  subscription_required_status?: {
    allowed: boolean;
    requires_subscription: boolean;
    reason?: 'sign_in_required' | 'paid_subscription_required' | null;
    current_plan?: string;
    allowed_plans?: string[];
  } | null;
  purchase_limit_status?: {
    pack_slug: string;
    max_purchases: number;
    window_days: number;
    purchases_used: number;
    remaining_purchases: number;
    allowed: boolean;
    window_active: boolean;
    window_started_at?: string | null;
    window_expires_at?: string | null;
  } | null;
  credit_unit_mode?: 'dobi' | string;
  unit_key?: string;
  unit_label_zh?: string;
  unit_label_en?: string;
  scale?: number;
  credits_per_image?: number;
  credit_unit?: CreditUnitMetadata | null;
}

export interface TopupPacksListResponse {
  success: boolean;
  currency: string;
  packs: TopupPackResponse[];
  subscription_discount_pct?: number;
  credit_unit_mode?: 'dobi' | string;
  unit_key?: string;
  unit_label_zh?: string;
  unit_label_en?: string;
  scale?: number;
  credits_per_image?: number;
  credit_unit?: CreditUnitMetadata | null;
}

export interface CancelSubscriptionPayload {
  reason?: string;
}

export interface CancelSubscriptionResponse {
  success: boolean;
  status: string;
  message: string;
}

export interface ReactivateSubscriptionResponse {
  success: boolean;
  message: string;
  subscription_id?: string;
}

export interface DebugResetSubscriptionResponse {
  success: boolean;
  message: string;
  user_id: string;
}

// ── API Client ──

class SubscriptionsApi extends ApiClient {
  /** 获取当前订阅状态 */
  async getCurrent(options?: { includePurchaseHistory?: boolean; signal?: AbortSignal }): Promise<ApiResponse<CurrentSubscriptionResponse>> {
    const query = options?.includePurchaseHistory === false
      ? '?include_purchase_history=false'
      : '';
    return this.request(`/subscriptions/current${query}`, { signal: options?.signal, cache: 'no-store' });
  }

  /** 获取套餐列表 */
  async getPlans(surface: SubscriptionPlanSurface = 'web'): Promise<ApiResponse<SubscriptionPlansListResponse>> {
    const query = surface === 'web' ? '' : `?surface=${encodeURIComponent(surface)}`;
    return this.request(`/subscriptions/plans${query}`);
  }

  async getModelAccess(modelKeys: string[]): Promise<ApiResponse<{
    items: Array<{ model_key: string; allowed: boolean }>;
  }>> {
    const query = new URLSearchParams();
    modelKeys.forEach(key => query.append('model_key', key));
    return this.request(`/subscriptions/entitlements/model-access?${query}`);
  }

  async getMembershipUses(
    kind: MembershipUseState['kind'],
    scopeKeys: string[],
  ): Promise<ApiResponse<{ kind: MembershipUseState['kind']; items: MembershipUseState[] }>> {
    const query = new URLSearchParams({ kind });
    scopeKeys.forEach((scopeKey) => query.append('scope_key', scopeKey));
    return this.request(`/subscriptions/entitlements/membership-uses?${query.toString()}`);
  }

  /** 获取加油包列表 */
  async getTopupPacks(): Promise<ApiResponse<TopupPacksListResponse>> {
    return this.request('/subscriptions/topup-packs');
  }

  /** 发起海外积分充值 */
  async topup(
    creditsAmount: number,
    options?: { returnProjectId?: string; couponClaimId?: string; returnUrl?: string; idempotencyKey?: string },
  ): Promise<ApiResponse<TopupResponse>> {
    const conversionOwner = captureXConversionOwner();
    const idempotencyKey = options?.idempotencyKey ?? `web:${generateId()}`;
    const result = await this.request<TopupResponse>('/subscriptions/topup', {
      method: 'POST',
      signal: boundedPaymentSignal(PAYMENT_MUTATION_TIMEOUT_MS),
      body: JSON.stringify({
        credits_amount: creditsAmount,
        return_project_id: options?.returnProjectId ?? null,
        return_url: options?.returnUrl ?? null,
        checkout_ui_mode: 'PAYMENTS',
        acquisition_visit: getAcquisitionVisitClaim(),
        coupon_claim_id: options?.couponClaimId ?? null,
        idempotency_key: idempotencyKey,
      }),
    });
    if (result.success && result.data?.success === true && result.data.order_id && result.data.mode === 'airwallex') {
      trackFunnelEvent('checkout_started', result.data.order_id);
      trackXCheckout(conversionOwner, result.data.order_id);
    }
    return result;
  }

  async getAdminMonthlyTestOffer(): Promise<ApiResponse<AdminMonthlyTestOffer>> {
    return this.request('/subscriptions/admin/monthly-test-offer', {
      signal: boundedPaymentSignal(PAYMENT_STATUS_TIMEOUT_MS),
      cache: 'no-store',
    });
  }

  /** 发起 Airwallex 订阅 checkout */
  async subscribeAirwallex(
    planSlug: string,
    priceVariantId: string,
    returnUrl?: string,
  ): Promise<ApiResponse<AirwallexCheckoutResponse>> {
    const conversionOwner = captureXConversionOwner();
    const result = await this.request<AirwallexCheckoutResponse>('/subscriptions/subscribe/airwallex', {
      method: 'POST',
      signal: boundedPaymentSignal(PAYMENT_MUTATION_TIMEOUT_MS),
      body: JSON.stringify({
        plan_slug: planSlug,
        price_variant_id: priceVariantId,
        return_url: returnUrl ?? null,
        checkout_ui_mode: 'PAYMENTS',
        acquisition_visit: getAcquisitionVisitClaim(),
      }),
    });
    if (result.success && result.data?.success === true && result.data.order_id && result.data.mode === 'airwallex') {
      trackFunnelEvent('checkout_started', result.data.order_id);
      trackXCheckout(conversionOwner, result.data.order_id);
    }
    return result;
  }

  /** 发起微信订阅支付 */
  async subscribeWechat(planSlug: string, returnUrl?: string): Promise<ApiResponse<WechatPaymentResponse>> {
    void planSlug;
    void returnUrl;
    throw new Error('Domestic payment routes are disabled on the overseas branch');
  }

  /** 发起微信委托代扣签约 */
  async subscribeWechatCycle(planSlug: string, returnUrl?: string): Promise<ApiResponse<WechatCycleSubscribeResponse>> {
    void planSlug;
    void returnUrl;
    throw new Error('Domestic payment routes are disabled on the overseas branch');
  }

  /** 发起微信续费支付 */
  async renewWechat(returnUrl?: string): Promise<ApiResponse<WechatPaymentResponse>> {
    void returnUrl;
    throw new Error('Domestic payment routes are disabled on the overseas branch');
  }

  /** 发起 Airwallex 订阅续费 checkout */
  async renewAirwallex(returnUrl?: string): Promise<ApiResponse<AirwallexCheckoutResponse>> {
    const conversionOwner = captureXConversionOwner();
    const result = await this.request<AirwallexCheckoutResponse>('/subscriptions/renew/airwallex', {
      method: 'POST',
      body: JSON.stringify({
        return_url: returnUrl ?? null,
        checkout_ui_mode: 'PAYMENTS',
        acquisition_visit: getAcquisitionVisitClaim(),
      }),
    });
    if (result.success && result.data?.success === true && result.data.order_id && result.data.mode === 'airwallex') {
      trackFunnelEvent('checkout_started', result.data.order_id);
      trackXCheckout(conversionOwner, result.data.order_id);
    }
    return result;
  }

  /** 发起支付宝积分充值 */
  async topupAlipay(creditsAmount: number, returnUrl: string, couponClaimId?: string): Promise<ApiResponse<AlipayPaymentResponse>> {
    void creditsAmount;
    void returnUrl;
    void couponClaimId;
    throw new Error('Domestic payment routes are disabled on the overseas branch');
  }

  /** 发起微信积分充值 */
  async topupWechat(creditsAmount: number, returnUrl?: string, couponClaimId?: string): Promise<ApiResponse<WechatPaymentResponse>> {
    void creditsAmount;
    void returnUrl;
    void couponClaimId;
    throw new Error('Domestic payment routes are disabled on the overseas branch');
  }

  async topupAlipayV2(
    creditsAmount: number,
    quantity: number,
    returnUrl: string,
    couponClaimId?: string,
    idempotencyKey?: string,
  ): Promise<ApiResponse<AlipayPaymentResponse>> {
    void creditsAmount;
    void quantity;
    void returnUrl;
    void couponClaimId;
    void idempotencyKey;
    throw new Error('Domestic payment routes are disabled on the overseas branch');
  }

  async topupWechatV2(
    creditsAmount: number,
    quantity: number,
    returnUrl?: string,
    couponClaimId?: string,
    idempotencyKey?: string,
  ): Promise<ApiResponse<WechatPaymentResponse>> {
    void creditsAmount;
    void quantity;
    void returnUrl;
    void couponClaimId;
    void idempotencyKey;
    throw new Error('Domestic payment routes are disabled on the overseas branch');
  }

  async getOpenWechatTopupOrder(): Promise<ApiResponse<{ success: boolean; order: OpenWechatTopupOrder | null }>> {
    throw new Error('Domestic payment routes are disabled on the overseas branch');
  }

  /** 查询微信委托代扣签约状态 */
  /**
   * ApiClient 会解开后端的 `{ success, data }` envelope，因此这里返回的
   * data 就是协议本身；没有有效协议时为 null。
   */
  async getWechatAgreement(): Promise<ApiResponse<AgreementStatusData | null>> {
    return this.request('/subscriptions/wechat/agreement');
  }

  async getWechatCycleSession(contractCode: string): Promise<ApiResponse<WechatCycleSessionData>> {
    void contractCode;
    throw new Error('Domestic payment routes are disabled on the overseas branch');
  }

  /** 解除微信委托代扣签约 */
  async unsignWechatAgreement(): Promise<ApiResponse<{ success: boolean; message: string }>> {
    throw new Error('Domestic payment routes are disabled on the overseas branch');
  }

  /** 查询支付订单状态（轮询用） */
  async getOrderStatus(orderId: string): Promise<ApiResponse<OrderStatusResponse>> {
    const conversionOwner = captureXConversionOwner();
    const result = await this.request<OrderStatusResponse>(`/subscriptions/order/${encodeURIComponent(orderId)}/status`, {
      signal: boundedPaymentSignal(PAYMENT_STATUS_TIMEOUT_MS),
    });
    if (result.success && result.data) trackXPurchase(conversionOwner, orderId, result.data);
    return result;
  }

  async listOrders(params?: { limit?: number; offset?: number }): Promise<ApiResponse<{ success: boolean; items: UserOrderSummary[]; total: number }>> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return this.request(`/subscriptions/orders${qs ? `?${qs}` : ''}`);
  }

  async getOrderDetail(orderId: string): Promise<ApiResponse<{ success: boolean; order: UserOrderSummary }>> {
    return this.request(`/subscriptions/order/${encodeURIComponent(orderId)}`, {
      signal: boundedPaymentSignal(PAYMENT_STATUS_TIMEOUT_MS),
    });
  }

  async getOpenSubscriptionOrder(): Promise<ApiResponse<{ order: UserOrderSummary | null }>> {
    return this.request('/subscriptions/orders/open-subscription', {
      signal: boundedPaymentSignal(PAYMENT_STATUS_TIMEOUT_MS),
    });
  }

  async completePayment(orderId: string, consentId?: string): Promise<ApiResponse<{ success: boolean; status: 'completed' | 'processing' }>> {
    return this.request(`/subscriptions/order/${encodeURIComponent(orderId)}/complete-payment`, {
      method: 'POST', body: JSON.stringify({ consent_id: consentId }),
      signal: boundedPaymentSignal(PAYMENT_MUTATION_TIMEOUT_MS),
    });
  }

  async getCheckoutSession(orderId: string): Promise<ApiResponse<AirwallexCheckoutSession>> {
    return this.request(`/subscriptions/order/${encodeURIComponent(orderId)}/checkout-session`, {
      method: 'POST',
      signal: boundedPaymentSignal(PAYMENT_MUTATION_TIMEOUT_MS),
    });
  }

  async getOrderAlipayPayUrl(orderId: string, returnUrl: string): Promise<ApiResponse<ExistingOrderAlipayPayUrlResponse>> {
    void orderId;
    void returnUrl;
    throw new Error('Domestic payment routes are disabled on the overseas branch');
  }

  /** Resume an existing Airwallex order through its original provider. */
  async resumeOrderCheckout(orderId: string, returnUrl: string, upgradeConfirmation?: { amount: number; currency: string }): Promise<ApiResponse<ResumeOrderCheckoutResponse>> {
    const conversionOwner = captureXConversionOwner();
    const result = await this.request<ResumeOrderCheckoutResponse>(`/subscriptions/order/${encodeURIComponent(orderId)}/resume-checkout`, {
      method: 'POST',
      signal: boundedPaymentSignal(PAYMENT_MUTATION_TIMEOUT_MS),
      body: JSON.stringify({ return_url: returnUrl, ...(upgradeConfirmation ? { confirm_upgrade: true, confirmed_amount: upgradeConfirmation.amount, confirmed_currency: upgradeConfirmation.currency } : {}) }),
    });
    if (result.success && result.data?.success === true && result.data.checkout_url) {
      trackFunnelEvent('checkout_started', orderId);
      trackXCheckout(conversionOwner, orderId);
    }
    return result;
  }

  async expireOrder(orderId: string): Promise<ApiResponse<ExpireOrderResponse>> {
    return this.request(`/subscriptions/order/${encodeURIComponent(orderId)}/expire`, {
      method: 'POST',
      signal: boundedPaymentSignal(PAYMENT_MUTATION_TIMEOUT_MS),
    });
  }

  async switchOrderPaymentMethod(
    orderId: string,
    targetProvider: 'alipay' | 'wechatpay' | null,
  ): Promise<ApiResponse<SwitchPaymentMethodResponse>> {
    void orderId;
    void targetProvider;
    throw new Error('Domestic payment routes are disabled on the overseas branch');
  }

  async listInvoices(params?: { limit?: number; offset?: number }): Promise<ApiResponse<{ success: boolean; items: InvoiceRequestSummary[]; total: number }>> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return this.request(`/subscriptions/invoices${qs ? `?${qs}` : ''}`);
  }

  async submitInvoice(orderId: string, payload: SubmitInvoicePayload): Promise<ApiResponse<{ success: boolean; invoice_request: InvoiceRequestSummary }>> {
    return this.request(`/subscriptions/order/${encodeURIComponent(orderId)}/invoice`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /** 过期当前用户超时未支付的 pending 订单 */
  async expireStaleOrders(): Promise<ApiResponse<{ expired_count: number }>> {
    return this.request('/subscriptions/orders/expire-stale', { method: 'POST' });
  }

  /** 取消自动续费 */
  async cancelAutoRenew(): Promise<ApiResponse<{ success: boolean }>> {
    return this.request('/subscriptions/cancel', { method: 'POST' });
  }

  /** 取消订阅或关闭自动续费 */
  async cancelSubscription(payload: CancelSubscriptionPayload): Promise<ApiResponse<CancelSubscriptionResponse>> {
    return this.request('/subscriptions/cancel', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /** 恢复未到期订阅的自动续费 */
  async reactivateSubscription(): Promise<ApiResponse<ReactivateSubscriptionResponse>> {
    return this.request('/subscriptions/reactivate', {
      method: 'POST',
    });
  }

  /** [开发调试] 重置订阅状态 */
  async debugResetSubscription(): Promise<ApiResponse<DebugResetSubscriptionResponse>> {
    return this.request('/subscriptions/debug/reset', {
      method: 'POST',
    });
  }
}

export const subscriptionsApi = new SubscriptionsApi();
